import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import Button from '~/shared/components/Buttons';
import { Input } from '~/shared/components/Input';
import { useXFetch } from '~/shared/utils/useXFetch';
import Loader from '~/shared/components/Loader';
import Typography from '~/shared/components/Typography';

import { SectionFooter } from './SectionFooter';

type RoleArnInfo = { roleArn: string };

export function RoleArn() {
  const [forceEditable, setForceEditable] = useState(false);
  const search = useLocation().search;
  const showDevTools = !!new URLSearchParams(search).get('devtools');
  const api = useXFetch();

  const {
    data: roleArn,
    isLoading: isQueryLoading,
    refetch,
  } = useQuery({
    queryKey: ['getRoleArn'],
    queryFn: async () => {
      const { roleArn } = await api<RoleArnInfo>('/api/v1/accounts/role-arn');
      reset({ roleArn });
      return roleArn;
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    reset,
    getValues,
    // setValue,
    formState: { isLoading, isSubmitting, errors, isValid, isDirty },
    control,
  } = useForm<RoleArnInfo>();

  const onSubmit: SubmitHandler<RoleArnInfo> = async (values) => {
    try {
      await api<RoleArnInfo>('/api/v1/accounts/role-arn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roleArn: values.roleArn,
        }),
      });
      refetch();
      setForceEditable(false);
    } catch (e) {
      setError('root', { message: (e as Error).message });
      setForceEditable(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="H5" color="text-primary-900" className="mb-[12px]">
        Your Role&apos;s ARN
      </Typography>
      {showDevTools && <DevTool control={control} />}
      <Input
        {...register('roleArn', { required: true })}
        disabled={(!forceEditable && roleArn && !errors?.root) || isLoading || isQueryLoading}
        placeholder="Paste here"
      />

      <SectionFooter
        infoProps={
          isLoading || isQueryLoading || isSubmitting
            ? {
                variant: 'none',
                text: (
                  <div className="h-[44px] flex items-center">
                    <Loader variant="sm" />
                  </div>
                ),
              }
            : errors.root
              ? {
                  text: 'Your ARN code was incorrect. Please copy the role ARN which has the trust relationship',
                  variant: 'error',
                }
              : forceEditable || roleArn
                ? {
                    text: (
                      <span>
                        ARN code Successfully added.{' '}
                        <span
                          onClick={() => {
                            setForceEditable(true);
                            reset(getValues(), {});
                          }}
                          className="text-[#999] underline cursor-pointer"
                        >
                          {' '}
                          Change code{' '}
                        </span>
                      </span>
                    ),
                    variant: 'success',
                  }
                : { text: 'Please copy the role ARN which has the trust relationship' }
        }
        buttonEl={
          <Button
            type="submit"
            variant="primary"
            loading={isLoading || isSubmitting}
            disabled={
              !isValid ||
              (!isDirty && roleArn && !forceEditable && !errors?.root) ||
              isLoading ||
              isQueryLoading
            }
            className="w-[121px] gap-[10px] disabled:opacity-30"
          >
            Confirm
          </Button>
        }
      />
    </form>
  );
}
